import { motion } from "framer-motion";
import { Calendar, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import type { DoulaListing } from "./types";
import { getMonthsArray as months } from "@/utils/helpers";

interface AvailabilityCardProps {
	listing: DoulaListing | null;
	monthsAvailable: {
		january: boolean;
		february: boolean;
		march: boolean;
		april: boolean;
		may: boolean;
		june: boolean;
		july: boolean;
		august: boolean;
		september: boolean;
		october: boolean;
		november: boolean;
		december: boolean;
	};
	savingMonths: boolean;
	onListingChange: (field: keyof DoulaListing, value: unknown) => void;
	onMonthToggle: (month: string) => void;
	onToggleSelectAllMonths: () => void;
	onSaveMonthsAvailable: () => void;
	introDays: {
		monday: { available: boolean; startTime: string; endTime: string };
		tuesday: { available: boolean; startTime: string; endTime: string };
		wednesday: { available: boolean; startTime: string; endTime: string };
		thursday: { available: boolean; startTime: string; endTime: string };
		friday: { available: boolean; startTime: string; endTime: string };
		saturday: { available: boolean; startTime: string; endTime: string };
		sunday: { available: boolean; startTime: string; endTime: string };
	};
	onIntroDayToggle: (day: string) => void;
	onIntroTimeChange: (
		day: string,
		field: "startTime" | "endTime",
		value: string,
	) => void;
}

export default function AvailabilityCard({
	listing,
	monthsAvailable,
	savingMonths,
	onListingChange,
	onMonthToggle,
	onToggleSelectAllMonths,
	onSaveMonthsAvailable,
	introDays,
	onIntroDayToggle,
	onIntroTimeChange,
}: AvailabilityCardProps) {

	const days = [
		{ key: "monday", label: "Mon", full: "Monday" },
		{ key: "tuesday", label: "Tue", full: "Tuesday" },
		{ key: "wednesday", label: "Wed", full: "Wednesday" },
		{ key: "thursday", label: "Thu", full: "Thursday" },
		{ key: "friday", label: "Fri", full: "Friday" },
		{ key: "saturday", label: "Sat", full: "Saturday" },
		{ key: "sunday", label: "Sun", full: "Sunday" },
	];

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.6, delay: 0.5 }}
			className="mt-8"
		>
			<Card className="glass-card border-0 shadow-xl">
				<CardHeader className="text-center pb-6">
					<div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
						<Calendar className="h-8 w-8 text-newTheme-orange" />
					</div>
					<CardTitle className="text-2xl font-bold text-black font-karla">
						Availability Settings
					</CardTitle>
					<p className="text-newTheme-grey font-karla mt-2 max-w-2xl mx-auto">
						Set your booking preferences and availability to help families plan
						ahead.
					</p>
				</CardHeader>
				<CardContent className="space-y-6">
					{/* Booking Window */}
					<div>
						<Label className="text-lg font-semibold text-newTheme-grey font-karla flex items-center">
							<Clock className="h-5 w-5 mr-2 text-newTheme-orange" />
							Booking Window
						</Label>
						<p className="text-sm text-newTheme-grey font-karla mt-1 mb-4">
							How many months ahead can families book with you?
						</p>
						<div className="bg-white p-4 rounded-lg border border-gray-200">
							<Label
								htmlFor="bookingWindow"
								className="text-sm font-medium text-newTheme-grey font-karla"
							>
								Months Ahead
							</Label>
							<Input
								id="bookingWindow"
								type="number"
								min="1"
								max="24"
								value={listing?.booking_window_months || 6}
								onChange={(e) =>
									onListingChange(
										"booking_window_months",
										parseInt(e.target.value) || 6,
									)
								}
								className="mt-1 w-32"
							/>
							<p className="text-xs text-newTheme-spruce font-karla mt-1">
								Recommended: 6-12 months for birth doula services
							</p>
						</div>
					</div>

					{/* Months Available */}
					<div>
						<div className="flex items-center justify-between mb-6">
							<div>
								<Label className="text-lg font-semibold text-newTheme-grey font-karla">
									Months Available
								</Label>
								<p className="text-sm text-gray-600 font-karla mt-1">
									Select the months when you're available to take on new clients
								</p>
							</div>
						</div>

						{/* Quick Selection */}
						<div className="flex items-center justify-between p-4 bg-sage-50 rounded-lg border border-sage-200 mb-6">
							<div>
								<h4 className="font-semibold text-newTheme-grey font-karla">
									Quick Selection
								</h4>
								<p className="text-sm text-gray-600 font-karla">
									Select all months at once
								</p>
							</div>
							<Button
								onClick={onToggleSelectAllMonths}
								variant="outline"
								className="border-newTheme-lightGrey text-black hover:bg-gray-50 shadow-btn-shadow hover:text-black"
							>
								{Object.values(monthsAvailable).every((v) => v)
									? "Deselect All Months"
									: "Select All Months"}
							</Button>
						</div>

						{/* Month Selection Grid */}
						<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
							{months.map((month) => (
								<div
									key={month.key}
									className={`
                    p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 border-newTheme-lightGrey
                    ${monthsAvailable[month.key as keyof typeof monthsAvailable]
											? "bg-newTheme-lightBlue"
											: "bg-newTheme-cotton"
										}
                  `}
									onClick={() => onMonthToggle(month.key)}
								>
									<div className="flex items-center space-x-3">
										<Checkbox
											checked={
												monthsAvailable[
												month.key as keyof typeof monthsAvailable
												]
											}
											onChange={() => onMonthToggle(month.key)}
										/>
										<div>
											<p className="font-medium text-newTheme-grey font-karla">
												{month.label}
											</p>
											<p className="text-sm text-newTheme-spruce font-karla">
												{month.short}
											</p>
										</div>
									</div>
								</div>
							))}
						</div>

						{/* Summary */}
						<div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
							<h4 className="font-semibold text-newTheme-grey font-karla mb-2">
								Summary
							</h4>
							<p className="text-sm text-gray-600 font-karla">
								You're available{" "}
								{Object.values(monthsAvailable).filter(Boolean).length} out of
								12 months.
								{Object.values(monthsAvailable).filter(Boolean).length ===
									0 && (
										<span className="text-red-600 ml-1">
											Please select at least one month.
										</span>
									)}
							</p>
						</div>

						<Button
							onClick={onSaveMonthsAvailable}
							disabled={
								savingMonths ||
								Object.values(monthsAvailable).filter(Boolean).length === 0
							}
							className="mt-4 bg-newTheme-lime hover:bg-newTheme-orange text-black shadow-btn-shadow"
						>
							{savingMonths ? "Saving..." : "Save Availability"}
						</Button>
					</div>

					{/* Intro Call Availability */}
					<div className="border-t border-gray-200 pt-8">
						<div className="mb-6">
							<Label className="text-lg font-semibold text-newTheme-grey font-karla flex items-center">
								<Clock className="h-5 w-5 mr-2 text-newTheme-orange" />
								Intro Call Availability
							</Label>
							<p className="text-sm text-newTheme-grey font-karla mt-1">
								Set your availability for initial consultation calls with
								potential clients
							</p>
						</div>

						{/* Days of the Week with Individual Times */}
						<div className="mb-6">
							<Label className="text-sm font-medium text-newTheme-grey font-karla mb-4 block">
								Available Days &amp; Times
							</Label>
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
								{days.map((day) => {
									const daySettings =
										introDays[day.key as keyof typeof introDays];
									return (
										<div
											key={day.key}
											className={`
                        p-4 rounded-lg border-2 transition-all duration-200 border-newTheme-lightGrey
                        ${daySettings.available
													? "bg-newTheme-cotton"
													: "bg-newTheme-lightBlue"
												}
                      `}
										>
											{/* Day Header */}
											<div
												className="flex items-center justify-between mb-3 cursor-pointer"
												onClick={() => onIntroDayToggle(day.key)}
											>
												<div className="text-center flex-1">
													<p className="font-medium text-newTheme-grey font-karla text-sm">
														{day.label}
													</p>
													<p className="text-xs text-gray-600 font-karla">
														{day.full}
													</p>
												</div>
												<Checkbox
													checked={daySettings.available}
													onChange={() => onIntroDayToggle(day.key)}
												/>
											</div>

											{/* Time Inputs - Only show when available */}
											{daySettings.available && (
												<div className="space-y-3">
													<div>
														<Label className="text-xs text-newTheme-grey font-karla">
															Start
														</Label>
														<Input
															type="time"
															value={daySettings.startTime}
															onChange={(e) =>
																onIntroTimeChange(
																	day.key,
																	"startTime",
																	e.target.value,
																)
															}
															className="mt-1 text-sm"
														/>
													</div>
													<div>
														<Label className="text-xs text-newTheme-grey font-karla">
															End
														</Label>
														<Input
															type="time"
															value={daySettings.endTime}
															onChange={(e) =>
																onIntroTimeChange(
																	day.key,
																	"endTime",
																	e.target.value,
																)
															}
															className="mt-1 text-sm"
														/>
													</div>
												</div>
											)}
										</div>
									);
								})}
							</div>
						</div>

						{/* Summary for intro calls */}
						<div className="mt-6 p-4 bg-newTheme-sky border border-newTheme-lightGrey rounded-lg">
							<h4 className="font-semibold text-newTheme-grey font-karla mb-2">
								Intro Call Summary
							</h4>
							<p className="text-sm text-gray-600 font-karla">
								Available{" "}
								{Object.values(introDays).filter((day) => day.available).length}{" "}
								days a week
								{Object.values(introDays).filter((day) => day.available)
									.length === 0 && (
										<span className="text-red-600 ml-1">
											Please select at least one day.
										</span>
									)}
							</p>
							{Object.values(introDays).filter((day) => day.available).length >
								0 && (
									<div className="mt-2 text-xs text-gray-500 font-karla">
										Each day can have custom time ranges for maximum flexibility
									</div>
								)}
						</div>
					</div>
				</CardContent>
			</Card>
		</motion.div>
	);
}
